import React, { useEffect, useState, useCallback } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { RootState } from '../store'
import { ArrowLeft, Loader2, RefreshCw, Send, Save, Ban, Sparkles } from 'lucide-react'
import toast from 'react-hot-toast'
import { isAdminUser } from '../utils/admin'
import socialService, { type SocialPost, type SocialPostStatus } from '../services/socialService'

type Platform = 'instagram' | 'facebook' | 'linkedin'
const PLATFORMS: Platform[] = ['instagram', 'facebook', 'linkedin']

const STATUS_STYLES: Record<SocialPostStatus, string> = {
  pending: 'bg-muted/40 text-muted-foreground',
  rendered: 'bg-amber-500/20 text-amber-500',
  published: 'bg-green-600/20 text-green-500',
  skipped: 'bg-muted/40 text-muted-foreground',
  failed: 'bg-red-500/20 text-red-500',
}

const AdminSocialPosters: React.FC = () => {
  const navigate = useNavigate()
  const { user } = useSelector((state: RootState) => state.auth)

  const [posts, setPosts] = useState<SocialPost[]>([])
  const [loading, setLoading] = useState(false)
  const [generating, setGenerating] = useState(false)
  const [busyId, setBusyId] = useState<string | null>(null)

  useEffect(() => {
    if (!isAdminUser(user)) {
      navigate('/dashboard', { replace: true })
    }
  }, [user, navigate])

  const load = useCallback(() => {
    if (!isAdminUser(user)) return
    setLoading(true)
    socialService
      .listPosts()
      .then((list) => setPosts(Array.isArray(list) ? list : []))
      .catch(() => setPosts([]))
      .finally(() => setLoading(false))
  }, [user])

  useEffect(() => {
    load()
  }, [load])

  const handleGenerate = async () => {
    setGenerating(true)
    try {
      const results = await socialService.generateTomorrow(false, false)
      toast.success(`Rendered ${results.length} poster(s) for tomorrow`)
      load()
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Failed to generate posters')
    } finally {
      setGenerating(false)
    }
  }

  const setCaption = (matchId: string, platform: Platform, value: string) => {
    setPosts((prev) =>
      prev.map((p) =>
        p.matchId === matchId
          ? { ...p, captions: { ...(p.captions || {}), [platform]: value } }
          : p,
      ),
    )
  }

  const handleSaveCaptions = async (post: SocialPost) => {
    setBusyId(post.matchId)
    try {
      await socialService.updateCaptions(post.matchId, post.captions || {})
      toast.success('Captions saved')
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Failed to save captions')
    } finally {
      setBusyId(null)
    }
  }

  const handlePublish = async (post: SocialPost) => {
    if (!window.confirm(`Publish "${post.posterData?.teamA} vs ${post.posterData?.teamB}" to all configured platforms now?`)) return
    setBusyId(post.matchId)
    try {
      const updated = await socialService.publish(post.matchId)
      setPosts((prev) => prev.map((p) => (p.matchId === post.matchId ? updated : p)))
      const published = Object.entries(updated.results || {})
        .filter(([, r]) => r.status === 'published')
        .map(([k]) => k)
      if (published.length) toast.success(`Published to: ${published.join(', ')}`)
      else toast.error('Nothing published — check platform credentials')
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Publish failed')
    } finally {
      setBusyId(null)
    }
  }

  const handleSkip = async (post: SocialPost) => {
    setBusyId(post.matchId)
    try {
      const updated = await socialService.skip(post.matchId)
      setPosts((prev) => prev.map((p) => (p.matchId === post.matchId ? updated : p)))
      toast.success('Marked as skipped')
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Failed to skip')
    } finally {
      setBusyId(null)
    }
  }

  if (!user || !isAdminUser(user)) return null

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border px-4 py-3 flex items-center gap-4">
        <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-muted-foreground hover:text-foreground text-sm font-medium">
          <ArrowLeft className="w-5 h-5" /> Back to Dashboard
        </Link>
        <h1 className="text-lg font-semibold text-foreground">Social posters (Admin)</h1>
      </header>

      <main className="container max-w-3xl mx-auto p-4">
        <div className="flex flex-wrap items-center gap-2 mb-6">
          <button
            type="button"
            onClick={handleGenerate}
            disabled={generating}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90 disabled:opacity-50"
          >
            {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            Generate tomorrow's posters
          </button>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-border text-sm font-medium hover:bg-muted/30 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>

        <p className="text-xs text-muted-foreground mb-4">
          Generating renders posters from community predictions and stores them for review. Edit the captions if you like, then
          “Approve &amp; publish” pushes to Instagram, Facebook and LinkedIn (only platforms with configured credentials).
        </p>

        {loading && (
          <div className="flex justify-center py-10">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        )}

        {!loading && posts.length === 0 && (
          <p className="text-muted-foreground py-10 text-center">
            No posters yet. Click “Generate tomorrow's posters”.
          </p>
        )}

        <div className="space-y-5">
          {!loading &&
            posts.map((post) => {
              const d = post.posterData || {}
              const busy = busyId === post.matchId
              const canPublish = post.status === 'rendered' || post.status === 'failed'
              return (
                <div key={post.matchId} className="glass-card rounded-xl p-4">
                  <div className="flex gap-4">
                    {post.imageUrl ? (
                      <a href={post.imageUrl} target="_blank" rel="noreferrer" className="shrink-0">
                        <img
                          src={post.imageUrl}
                          alt="poster"
                          className="w-28 sm:w-32 rounded-lg border border-border object-contain bg-black/20"
                        />
                      </a>
                    ) : (
                      <div className="w-28 sm:w-32 h-36 sm:h-40 rounded-lg border border-border bg-muted/20 flex items-center justify-center text-xs text-muted-foreground shrink-0">
                        No image
                      </div>
                    )}

                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="font-semibold text-foreground truncate">
                            {d.teamA} <span className="text-muted-foreground">vs</span> {d.teamB}
                          </p>
                          <p className="text-sm text-muted-foreground truncate">
                            {d.leagueLabel}
                            {d.dateLabel ? ` · ${d.dateLabel}` : ''}
                            {d.timeLabel ? ` ${d.timeLabel}` : ''}
                          </p>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {typeof d.totalPredictions === 'number' ? `${d.totalPredictions} predictions` : ''}
                          </p>
                        </div>
                        <span className={`px-2 py-1 rounded-md text-xs font-semibold capitalize ${STATUS_STYLES[post.status]}`}>
                          {post.status}
                        </span>
                      </div>

                      {post.results && (
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          {Object.entries(post.results).map(([platform, r]) => (
                            <span
                              key={platform}
                              title={r.error || r.postId || ''}
                              className={`px-2 py-0.5 rounded text-[11px] font-medium ${
                                r.status === 'published'
                                  ? 'bg-green-600/20 text-green-500'
                                  : r.status === 'failed'
                                    ? 'bg-red-500/20 text-red-500'
                                    : 'bg-muted/40 text-muted-foreground'
                              }`}
                            >
                              {platform}: {r.status}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="mt-4 space-y-3">
                    {PLATFORMS.map((platform) => (
                      <div key={platform}>
                        <label className="block text-xs font-medium text-muted-foreground mb-1 capitalize">{platform}</label>
                        <textarea
                          value={post.captions?.[platform] || ''}
                          onChange={(e) => setCaption(post.matchId, platform, e.target.value)}
                          rows={3}
                          className="w-full text-sm rounded-lg border border-border bg-background/60 p-2 text-foreground focus:outline-none focus:ring-1 focus:ring-primary resize-y"
                        />
                      </div>
                    ))}
                  </div>

                  <div className="flex flex-wrap gap-2 mt-3">
                    <button
                      type="button"
                      onClick={() => handleSaveCaptions(post)}
                      disabled={busy}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border text-sm font-medium hover:bg-muted/30 disabled:opacity-50"
                    >
                      <Save className="w-4 h-4" /> Save captions
                    </button>
                    <button
                      type="button"
                      onClick={() => handlePublish(post)}
                      disabled={busy || !canPublish}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-green-700 text-white text-sm font-semibold hover:bg-green-600 disabled:opacity-50"
                    >
                      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                      {post.status === 'published' ? 'Published' : 'Approve & publish'}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleSkip(post)}
                      disabled={busy || post.status === 'published'}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border text-sm font-medium text-muted-foreground hover:bg-muted/30 disabled:opacity-50"
                    >
                      <Ban className="w-4 h-4" /> Skip
                    </button>
                  </div>
                </div>
              )
            })}
        </div>
      </main>
    </div>
  )
}

export default AdminSocialPosters
